import { type FC, useState } from "react"
import { useDropzone } from "react-dropzone"
import styled from "styled-components"

import { useFileUrl } from "@forento/shared-client/hooks/useFileUrl"
import { removeArrayIndex, replaceArrayValue } from "@forento/shared/utilities/array"
import { getDataUrlByFile, getExtensionByFileName, supportedFileExtensions } from "@forento/shared/utilities/file"
import { parseNumber } from "@forento/shared/utilities/number"

import Button, { SubmitButton } from "~/themes/original/components/Button"
import Dropdown from "~/themes/original/components/Dropdown"
import { DeleteIcon, UploadFileIcon, UploadIcon } from "~/themes/original/components/Icon"
import InputField, { Label, useRichTextArea } from "~/themes/original/components/InputField"
import Modal, { ModalButtons, ModalTitle } from "~/themes/original/components/Modal"
import { useTranslation } from "~/translations"
import { dangerColor } from "~/utilities/styles"
import trpc, { query } from "~/utilities/trpc"

type Props = { channel: { id: number | null; name: string }; onPostCreated(): void }
const CreatePost: FC<Props> = ({ channel, onPostCreated }) => {
	const t = useTranslation()

	const categories = query.community.listPostCategories.useQuery()

	const [isOpen, setOpen] = useState(false)
	const [title, setTitle] = useState("")
	const content = useRichTextArea({ label: "Text" })
	const [images, setImages] = useState<File[]>([])
	const [categoryId, setCategoryId] = useState<string | null>(null)
	const [pollOptions, setPollOptions] = useState<string[]>([])
	const [attachments, setAttachments] = useState<{ label: string; file: File }[]>([])
	const [isSubmitting, setSubmitting] = useState(false)

	const imagesDropzone = useDropzone({
		accept: Object.fromEntries(supportedFileExtensions.image.map(x => [`image/${x.toLowerCase()}`, []])),
		onDrop: files => setImages(current => [...current, ...files]),
	})
	const attachmentsDropzone = useDropzone({
		onDrop: async files => {
			for (const file of files) {
				const dialog = prompt("Add attachment", file.name)
				if (dialog === null) continue
				setAttachments(current => [...current, { label: dialog, file }])
			}
		},
	})

	const handleSubmit = async () => {
		const exportedContent = content.exportEditorState()
		if (title.trim().length === 0 || exportedContent === null) return

		setSubmitting(true)

		try {
			await trpc.community.createPost.mutate({
				channelId: channel.id,
				title: title.trim(),
				content: exportedContent,
				imageDataUrls: await Promise.all(images.map(async image => await getDataUrlByFile(image))),
				categoryId: parseNumber(categoryId),
				pollOptions,
				attachments: await Promise.all(
					attachments.map(async attachment => ({
						label: attachment.label,
						dataUrl: await getDataUrlByFile(attachment.file),
						extension: getExtensionByFileName(attachment.file.name) ?? "",
					})),
				),
			})

			setTitle("")
			content.set(null)
			setCategoryId(null)
			setPollOptions([])
			setAttachments([])
			setOpen(false)
			onPostCreated()
		} catch (error) {
			console.error(error)
		} finally {
			setSubmitting(false)
		}
	}

	return (
		<Container>
			<Button variant="primary" onClick={() => setOpen(true)}>
				Create a new post
			</Button>
			<Modal isOpen={isOpen} onSubmit={handleSubmit} width={800}>
				<ModalTitle>Create a new post</ModalTitle>
				<InputField label="Title" value={title} onChange={setTitle} />
				{content.element}
				<div>
					<Label>{t("community.post.images")}</Label>
					<div className="flex flex-wrap gap-4">
						{images.map((image, index) => (
							<UploadedImage
								key={index}
								image={image}
								onDelete={() => setImages(current => removeArrayIndex(current, index))}
							/>
						))}
						<div
							className="relative flex h-24 w-24 cursor-pointer items-center justify-center rounded-lg border border-dashed border-primary break-all"
							{...imagesDropzone.getRootProps()}
						>
							<input {...imagesDropzone.getInputProps()} />
							<UploadIcon className="h-6 w-6 text-primary" />
						</div>
					</div>
				</div>
				<div>
					<Label>{t("community.post.category")}</Label>
					<Dropdown
						items={[
							{ id: "none", title: "-- No category --" },
							...(categories.data?.map(category => ({
								id: category.id.toString(),
								title: category.label,
							})) ?? []),
							{ id: "create", title: "Create new category" },
						]}
						selectedItemId={categoryId ?? "none"}
						onChange={id => setCategoryId(id === "none" ? null : id)}
					/>
				</div>
				<PollOptions>
					{pollOptions.map((option, index) => (
						<PollOption key={index}>
							<PollOptionInput
								value={option}
								onChange={value => setPollOptions(current => replaceArrayValue(current, index, value))}
							/>
							<PollOptionDeleteButton
								onClick={() => setPollOptions(current => removeArrayIndex(current, index))}
							>
								<DeleteIcon />
							</PollOptionDeleteButton>
						</PollOption>
					))}
					<Button variant="secondary" onClick={() => setPollOptions(current => [...current, ""])}>
						Add poll option
					</Button>
				</PollOptions>
				<div>
					<Label>{t("community.post.attachments")}</Label>
					<div className="flex flex-wrap gap-4">
						{attachments.map((attachment, index) => (
							<div
								key={index}
								className="relative flex items-center justify-center rounded-lg border border-dashed border-primary p-8 break-all"
							>
								<button
									type="button"
									className="absolute top-2 right-2 cursor-pointer rounded-full bg-black/50 p-2 transition-all duration-100 hover:bg-black/70"
									onClick={() => setAttachments(current => removeArrayIndex(current, index))}
								>
									<DeleteIcon className="h-4 w-4 text-danger" />
								</button>
								{attachment.label}
							</div>
						))}
						<div
							{...attachmentsDropzone.getRootProps()}
							className="flex cursor-pointer items-center justify-center rounded-lg border border-dashed border-primary p-8"
						>
							<input {...attachmentsDropzone.getInputProps()} />
							<UploadFileIcon className="h-6 w-6 text-primary" />
						</div>
					</div>
				</div>
				<ModalButtons>
					<Button variant="secondary" isDisabled={isSubmitting} onClick={() => setOpen(false)}>
						Cancel
					</Button>
					<SubmitButton variant="primary" isLoading={isSubmitting}>
						Create post
					</SubmitButton>
				</ModalButtons>
			</Modal>
		</Container>
	)
}

const UploadedImage: FC<{ image: File; onDelete(): void }> = ({ image, onDelete }) => {
	const imageUrl = useFileUrl(image)

	return (
		<div className="relative">
			<img src={imageUrl} className="h-24 rounded-lg" />
			<button
				type="button"
				className="absolute top-2 right-2 cursor-pointer rounded-full bg-black/50 p-2 transition-all duration-100 hover:bg-black/70"
				onClick={onDelete}
			>
				<DeleteIcon className="h-4 w-4 text-danger" />
			</button>
		</div>
	)
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	margin-bottom: 32px;
`

const PollOptions = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`

const PollOption = styled.div`
	display: flex;
	gap: 16px;
`

const PollOptionInput = styled(InputField)`
	flex: 1;
`

const PollOptionDeleteButton = styled(Button)`
	width: 52px;
	height: 52px;
	background-color: ${dangerColor};
	color: white;
	padding: 8px;
	border-radius: 4px;
`

export default CreatePost
